import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";
import {
  LayoutDashboard, FileText, AlertTriangle, CheckCircle2, ClipboardCheck,
  MessageSquareWarning, Truck, Ruler, Shield, Plus, Pencil, Trash2,
  Loader2, Search, Eye, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/quality-system`;

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "docs", label: "Documentos", icon: FileText },
  { id: "product-sheets", label: "Fichas Produto", icon: FileText },
  { id: "fmea", label: "FMEA", icon: AlertTriangle },
  { id: "control-plans", label: "Planos Controlo", icon: ClipboardCheck },
  { id: "spc", label: "SPC", icon: Ruler },
  { id: "ncs", label: "Não Conformidades", icon: AlertTriangle },
  { id: "capas", label: "Ações Corretivas", icon: CheckCircle2 },
  { id: "8d", label: "8D", icon: Shield },
  { id: "audits", label: "Auditorias", icon: ClipboardCheck },
  { id: "complaints", label: "Reclamações", icon: MessageSquareWarning },
  { id: "suppliers", label: "Fornecedores", icon: Truck },
  { id: "calibration", label: "Calibração", icon: Ruler },
  { id: "risks", label: "Riscos", icon: Shield },
  { id: "costs", label: "Custos Qualidade", icon: LayoutDashboard },
  { id: "improvements", label: "Planos Melhoria", icon: CheckCircle2 },
];

const severityColors = { menor: "bg-amber-500", maior: "bg-orange-500", critica: "bg-red-600" };
const statusColors = { aberta: "bg-red-500", em_analise: "bg-amber-500", em_tratamento: "bg-blue-500", em_curso: "bg-blue-500", fechada: "bg-green-500", concluida: "bg-green-500", verificada: "bg-green-600", cancelada: "bg-slate-400", planeada: "bg-blue-400", aprovado: "bg-green-500", rascunho: "bg-slate-400", em_revisao: "bg-amber-500", obsoleto: "bg-red-300", respondida: "bg-blue-500", ativo: "bg-green-500", suspenso: "bg-amber-500", bloqueado: "bg-red-500", conforme: "bg-green-500", nao_conforme: "bg-red-500", em_calibracao: "bg-blue-500", fora_servico: "bg-slate-500", identificado: "bg-blue-400", em_tratamento: "bg-amber-500", mitigado: "bg-green-500", aceite: "bg-slate-400" };
const riskColors = { baixo: "bg-green-500", medio: "bg-amber-500", alto: "bg-orange-500", critico: "bg-red-600" };

function StatusBadge({ status }) {
  return <Badge className={`${statusColors[status] || "bg-slate-400"} text-white text-xs`}>{(status || "").replace(/_/g, " ").toUpperCase()}</Badge>;
}

// ===== DASHBOARD =====
function QualityDashboard({ dashboard }) {
  if (!dashboard) return null;
  const { ncs, capas, audits, complaints, risks, calibration } = dashboard;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "NCs Abertas", value: ncs.abertas, color: "text-red-600", total: ncs.total },
          { label: "CAPAs em Atraso", value: capas.em_atraso, color: "text-orange-600", total: capas.total },
          { label: "Auditorias Plan.", value: audits.planeadas, color: "text-blue-600", total: audits.total },
          { label: "Reclamações", value: complaints.abertas, color: "text-amber-600", total: complaints.total },
          { label: "Riscos Críticos", value: risks.criticos + risks.altos, color: "text-red-600", total: risks.total },
          { label: "Calibrações Venc.", value: calibration.vencidas, color: "text-purple-600", total: calibration.total },
        ].map(k => (
          <Card key={k.label}><CardContent className="pt-4 text-center">
            <p className="text-xs text-muted-foreground">{k.label}</p>
            <p className={`text-3xl font-bold ${k.color}`}>{k.value}</p>
            <p className="text-xs text-slate-400">de {k.total}</p>
          </CardContent></Card>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Card><CardHeader><CardTitle className="text-sm">NCs por Severidade</CardTitle></CardHeader><CardContent>
          <div className="space-y-2">
            {[["Crítica", ncs.por_severidade.critica, "bg-red-500"], ["Maior", ncs.por_severidade.maior, "bg-orange-500"], ["Menor", ncs.por_severidade.menor, "bg-amber-500"]].map(([l,v,c]) => (
              <div key={l} className="flex items-center justify-between"><span className="text-sm">{l}</span><div className="flex items-center gap-2"><div className={`h-2 ${c} rounded`} style={{width: `${Math.min(v*20,100)}px`}} /><span className="font-bold text-sm">{v}</span></div></div>
            ))}
          </div>
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Ações Corretivas</CardTitle></CardHeader><CardContent>
          <div className="space-y-2">
            {[["Abertas", capas.abertas, "text-red-600"], ["Em Curso", capas.em_curso, "text-blue-600"], ["Concluídas", capas.concluidas, "text-green-600"]].map(([l,v,c]) => (
              <div key={l} className="flex items-center justify-between"><span className="text-sm">{l}</span><span className={`font-bold ${c}`}>{v}</span></div>
            ))}
          </div>
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Resumo Geral</CardTitle></CardHeader><CardContent>
          <div className="space-y-2">
            {[["Auditorias", audits.concluidas + "/" + audits.total], ["Reclamações fechadas", complaints.fechadas + "/" + complaints.total], ["Riscos mitigados", (risks.total - risks.criticos - risks.altos) + "/" + risks.total]].map(([l,v]) => (
              <div key={l} className="flex items-center justify-between"><span className="text-sm">{l}</span><span className="font-bold text-sm">{v}</span></div>
            ))}
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

// ===== GENERIC CRUD TABLE =====
function CrudModule({ title, icon: Icon, endpoint, columns, formFields, searchFields, enableImages, enablePdf }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);

  const fetch_ = async () => {
    try { setItems((await axios.get(`${API}/${endpoint}`)).data); } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { fetch_(); }, []);

  const openCreate = () => { setEditingId(null); setFormData({}); setImages([]); setShowForm(true); };
  const openEdit = (item) => { setEditingId(item.id); setFormData({...item}); setImages(item.images || []); setShowForm(true); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImg = { id: r.data.id, filename: r.data.original_filename, url: `${process.env.REACT_APP_BACKEND_URL}/api/quality-system/files/${r.data.id}` };
      setImages(prev => [...prev, newImg]);
      setFormData(prev => ({ ...prev, images: [...(prev.images || []), newImg] }));
      toast.success("Imagem carregada!");
    } catch (e) {
      console.error("Upload error:", e);
      showError(e, "Erro ao carregar imagem");
    }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleExportPdf = (item) => {
    const imagesHtml = (item.images || []).map(img =>
      `<div style="margin:15px 0;text-align:center;"><img src="${img.url}" style="max-width:100%;max-height:400px;border:1px solid #e5e7eb;border-radius:4px;" /><p style="font-size:10px;color:#9ca3af;margin-top:4px;">${img.filename || ""}</p></div>`
    ).join("");

    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/logo-ep3d.png`;
    const html = `<!DOCTYPE html><html><head><title>${item.code} - ${item.title}</title>
    <style>
      @page{size:A4;margin:15mm}
      body{font-family:Arial,sans-serif;color:#1e293b;font-size:11px;line-height:1.5}
      .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #f59e0b;padding-bottom:10px;margin-bottom:20px}
      .header img{height:50px}
      .doc-info{text-align:right;font-size:10px;color:#64748b}
      .doc-info strong{color:#0f172a}
      h1{font-size:18px;color:#0f172a;margin:0 0 15px}
      .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:20px;background:#f8fafc;padding:12px;border-radius:6px;font-size:10px}
      .meta div{padding:4px 0;border-bottom:1px solid #e2e8f0}
      .meta .label{color:#64748b;font-weight:600}
      .meta .value{color:#0f172a;font-weight:500}
      .section{margin:20px 0}
      .section h2{font-size:13px;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px}
      .section p{white-space:pre-wrap}
      .signatures{display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-top:40px;padding-top:15px;border-top:2px solid #e2e8f0}
      .sig{text-align:center;font-size:10px}
      .sig .line{border-top:1px solid #0f172a;margin-top:40px;padding-top:6px}
      .footer{margin-top:30px;padding-top:10px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:9px;text-align:center}
      .badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:9px;font-weight:700;text-transform:uppercase}
      .aprovado{background:#dcfce7;color:#166534}.rascunho{background:#f1f5f9;color:#475569}.em_revisao{background:#fef3c7;color:#92400e}.obsoleto{background:#fecaca;color:#991b1b}
    </style></head><body>
    <div class="header">
      <img src="${logoUrl}" alt="EP3D" onerror="this.outerHTML='<div style=\\'font-size:22px;font-weight:900;color:#0f172a\\'>EP3D <span style=\\'color:#f59e0b\\'>Produção</span></div>'" />
      <div class="doc-info">
        <strong>${item.code}</strong><br>
        Versão: ${item.version || "1.0"}<br>
        <span class="badge ${item.status || "rascunho"}">${(item.status || "rascunho").replace(/_/g, " ")}</span>
      </div>
    </div>
    <h1>${item.title}</h1>
    <div class="meta">
      <div><span class="label">Tipo:</span> <span class="value">${item.doc_type || "-"}</span></div>
      <div><span class="label">Categoria:</span> <span class="value">${item.category || "-"}</span></div>
      <div><span class="label">Elaborado por:</span> <span class="value">${item.created_by || "-"}</span></div>
      <div><span class="label">Data Criação:</span> <span class="value">${item.created_at ? new Date(item.created_at).toLocaleDateString("pt-PT") : "-"}</span></div>
      <div><span class="label">Verificado por:</span> <span class="value">${item.verified_by || "-"}</span></div>
      <div><span class="label">Aprovado por:</span> <span class="value">${item.approved_by || "-"}</span></div>
      <div><span class="label">Data Aprovação:</span> <span class="value">${item.approval_date || item.approved_at || "-"}</span></div>
      <div><span class="label">Próx. Revisão:</span> <span class="value">${item.next_review_date || "-"}</span></div>
      <div><span class="label">Referências:</span> <span class="value">${item.references || "-"}</span></div>
      <div><span class="label">Distribuição:</span> <span class="value">${item.distribution || "-"}</span></div>
    </div>
    ${item.objective ? `<div class="section"><h2>Objetivo</h2><p>${item.objective}</p></div>` : ""}
    ${item.scope ? `<div class="section"><h2>Âmbito</h2><p>${item.scope}</p></div>` : ""}
    ${item.content ? `<div class="section"><h2>Conteúdo</h2><p>${item.content}</p></div>` : ""}
    ${imagesHtml ? `<div class="section"><h2>Anexos / Imagens</h2>${imagesHtml}</div>` : ""}
    ${item.revision_notes ? `<div class="section"><h2>Notas da Revisão</h2><p>${item.revision_notes}</p></div>` : ""}
    <div class="signatures">
      <div class="sig"><div class="line">Elaborado por<br>${item.created_by || "_______________"}</div></div>
      <div class="sig"><div class="line">Verificado por<br>${item.verified_by || "_______________"}</div></div>
      <div class="sig"><div class="line">Aprovado por<br>${item.approved_by || "_______________"}</div></div>
    </div>
    <div class="footer"><p>EP3D Produção — ${item.code} v${item.version || "1.0"} — Gerado em ${new Date().toLocaleString("pt-PT")}</p></div>
    </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${item.code}_${item.title.replace(/\s+/g, "_")}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    try {
      if (editingId) { await axios.put(`${API}/${endpoint}/${editingId}`, formData); toast.success("Atualizado!"); }
      else { await axios.post(`${API}/${endpoint}`, formData); toast.success("Criado!"); }
      setShowForm(false); fetch_();
    } catch (e) { showError(e, "Erro"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar?")) return;
    try { await axios.delete(`${API}/${endpoint}/${id}`); toast.success("Eliminado"); fetch_(); } catch (e) { toast.error("Erro"); }
  };

  const filtered = items.filter(i => !search || searchFields.some(f => (i[f] || "").toLowerCase().includes(search.toLowerCase())));

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button onClick={openCreate} className="gap-2"><Plus className="h-4 w-4" /> Novo {title}</Button>
      </div>

      <Card><CardContent className="pt-4">
        <Table>
          <TableHeader><TableRow>
            {columns.map(c => <TableHead key={c.key}>{c.label}</TableHead>)}
            <TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-8 text-muted-foreground">Sem registos</TableCell></TableRow>
            ) : filtered.map(item => (
              <TableRow key={item.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setShowDetail(item)}>
                {columns.map(c => (
                  <TableCell key={c.key}>
                    {c.render ? c.render(item[c.key], item) : c.key === "status" || c.key === "severity" || c.key === "risk_level" ? <StatusBadge status={item[c.key]} /> : <span className={c.mono ? "font-mono font-bold" : ""}>{item[c.key] || "-"}</span>}
                  </TableCell>
                ))}
                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    {enablePdf && <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={() => handleExportPdf(item)} title="Exportar PDF"><FileText className="h-3.5 w-3.5" /></Button>}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar" : "Novo"} {title}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {formFields.map(f => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs font-semibold">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                {f.type === "select" ? (
                  <select value={formData[f.key] || ""} onChange={e => setFormData({...formData, [f.key]: e.target.value})} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm">
                    <option value="">Selecione...</option>
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea value={formData[f.key] || ""} onChange={e => setFormData({...formData, [f.key]: e.target.value})} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder={f.placeholder || ""} />
                ) : (
                  <Input type={f.type || "text"} value={formData[f.key] || ""} onChange={e => setFormData({...formData, [f.key]: f.type === "number" ? parseInt(e.target.value) || 0 : e.target.value})} placeholder={f.placeholder || ""} />
                )}
              </div>
            ))}
            {enableImages && (
              <div className="space-y-2 border-t pt-3">
                <Label className="text-xs font-semibold">Imagens / Anexos</Label>
                <div className="flex gap-2 flex-wrap">
                  {images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img.url} alt={img.filename} className="h-16 w-16 object-cover rounded border" />
                      <button type="button" onClick={() => { setImages(prev => prev.filter((_, j) => j !== i)); setFormData(prev => ({...prev, images: prev.images?.filter((_, j) => j !== i)})); }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center">x</button>
                      <p className="text-[8px] text-center truncate w-16">{img.filename}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*,.pdf" onChange={handleImageUpload} className="hidden" />
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-md text-xs font-medium hover:bg-blue-100">
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                      {uploading ? "A carregar..." : "Adicionar Imagem"}
                    </span>
                  </label>
                </div>
              </div>
            )}
            <Button className="w-full mt-4" onClick={handleSubmit}>{editingId ? "Guardar" : "Criar"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle><Icon className="h-4 w-4 inline mr-2" />{showDetail?.code || showDetail?.title || title}</DialogTitle></DialogHeader>
          {showDetail && (
            <div className="space-y-3 mt-2">
              {Object.entries(showDetail).filter(([k]) => !["id","_id","revision_history","attachments","corrective_action_ids","nc_ids","issues","history","findings","images"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b pb-1">
                  <span className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="text-sm font-medium text-right max-w-[60%] truncate">{typeof v === "object" ? JSON.stringify(v) : String(v || "-")}</span>
                </div>
              ))}
              {showDetail.images && showDetail.images.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Imagens / Anexos</p>
                  <div className="grid grid-cols-2 gap-2">
                    {showDetail.images.map((img, i) => (
                      <div key={i} className="border rounded overflow-hidden">
                        <img src={img.url} alt={img.filename} className="w-full object-contain max-h-[200px]" />
                        <p className="text-[10px] text-center text-muted-foreground p-1">{img.filename}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {enablePdf && <Button className="w-full mt-2 gap-2" variant="outline" onClick={() => { handleExportPdf(showDetail); }}><FileText className="h-4 w-4" /> Exportar PDF</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== MAIN PAGE =====
export default function ISO9001() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard`).then(r => setDashboard(r.data)).catch(() => {});
  }, [activeTab]);

  return (
    <div className="space-y-6" data-testid="iso9001-page">
      <div>
        <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3">
          <Shield className="h-7 w-7" /> Gestão da Qualidade
        </h1>
        <p className="text-muted-foreground mt-1">Sistema integrado de gestão da qualidade</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b pb-1 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap ${activeTab === tab.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
              data-testid={`tab-${tab.id}`}>
              <Icon className="h-3.5 w-3.5" />{tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === "dashboard" && <QualityDashboard dashboard={dashboard} />}

      {activeTab === "docs" && (
        <CrudModule title="Documento" icon={FileText} endpoint="documents" enableImages enablePdf
          searchFields={["code","title","doc_type","category","created_by","approved_by"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "doc_type", label: "Tipo" },
            { key: "category", label: "Categoria" },
            { key: "version", label: "Versão" },
            { key: "status", label: "Estado" },
            { key: "approved_by", label: "Aprovado por" },
          ]}
          formFields={[
            { key: "title", label: "Título do Documento", required: true, placeholder: "Ex: Manual da Qualidade SGQ" },
            { key: "doc_type", label: "Tipo de Documento", type: "select", required: true, options: [
              { value: "manual_qualidade", label: "Manual da Qualidade (MSGQ)" },
              { value: "procedimento", label: "Procedimento da Qualidade (PQ)" },
              { value: "instrucao_trabalho", label: "Instrução de Trabalho (IT)" },
              { value: "formulario", label: "Formulário / Modelo (AN)" },
              { value: "registo", label: "Registo da Qualidade" },
              { value: "politica", label: "Política" },
              { value: "matriz_registo", label: "Matriz de Registo (MR)" },
              { value: "plano_controlo", label: "Plano de Controlo" },
              { value: "especificacao_tecnica", label: "Especificação Técnica" },
              { value: "norma_externa", label: "Norma / Documento Externo" },
            ]},
            { key: "category", label: "Categoria / Processo Associado", type: "select", options: [
              { value: "sgq", label: "Sistema Gestão Qualidade (SGQ)" },
              { value: "producao", label: "Produção" },
              { value: "qualidade", label: "Qualidade / Controlo" },
              { value: "vendas", label: "Vendas / Comercial" },
              { value: "tecnico", label: "Departamento Técnico" },
              { value: "manutencao", label: "Manutenção" },
              { value: "logistica", label: "Logística / Expedição" },
              { value: "compras", label: "Compras / Aquisição" },
              { value: "rh", label: "Gestão de Pessoas / RH" },
              { value: "ti", label: "Tecnologia da Informação" },
              { value: "financeiro", label: "Administrativo / Financeiro" },
              { value: "armazem", label: "Armazém / Almoxarifado" },
              { value: "ambiental", label: "Ambiental / Segurança" },
            ]},
            { key: "scope", label: "Âmbito / Escopo", type: "textarea", placeholder: "Definir o âmbito de aplicação do documento" },
            { key: "objective", label: "Objetivo", type: "textarea", placeholder: "Descrever o objetivo do documento" },
            { key: "content", label: "Conteúdo / Descrição", type: "textarea", placeholder: "Conteúdo principal do documento..." },
            { key: "references", label: "Referências Normativas", placeholder: "Ex: ISO 9001:2015, PQ-001, IT-005" },
            { key: "related_processes", label: "Processos Relacionados", placeholder: "Ex: Produção, Qualidade, Manutenção" },
            { key: "version", label: "Versão / Revisão", placeholder: "Ex: 1.0, 02" },
            { key: "status", label: "Estado do Documento", type: "select", options: [
              { value: "rascunho", label: "Rascunho" },
              { value: "em_revisao", label: "Em Revisão" },
              { value: "aprovado", label: "Aprovado" },
              { value: "obsoleto", label: "Obsoleto" },
            ]},
            { key: "created_by", label: "Elaborado por", placeholder: "Nome de quem criou o documento" },
            { key: "verified_by", label: "Verificado por", placeholder: "Nome de quem verificou" },
            { key: "approved_by", label: "Aprovado por", placeholder: "Nome de quem aprovou" },
            { key: "approval_date", label: "Data de Aprovação", type: "date" },
            { key: "next_review_date", label: "Próxima Revisão", type: "date" },
            { key: "distribution", label: "Distribuição / Acesso", placeholder: "Ex: Todos os departamentos, Apenas Qualidade" },
            { key: "revision_notes", label: "Notas da Revisão / Alterações", type: "textarea", placeholder: "Descrever as alterações feitas nesta revisão" },
          ]}
        />
      )}

      {activeTab === "ncs" && (
        <CrudModule title="Não Conformidade" icon={AlertTriangle} endpoint="non-conformities"
          searchFields={["code","title","description","product_or_process","responsible"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "nc_type", label: "Tipo" },
            { key: "origin", label: "Origem" },
            { key: "severity", label: "Severidade" },
            { key: "status", label: "Estado" },
            { key: "responsible", label: "Responsável" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "description", label: "Descrição", type: "textarea", required: true },
            { key: "nc_type", label: "Tipo", type: "select", required: true, options: [
              { value: "defeito", label: "Defeito" }, { value: "erro_producao", label: "Erro de Produção" },
              { value: "reclamacao_cliente", label: "Reclamação Cliente" }, { value: "falha_interna", label: "Falha Interna" },
              { value: "desvio_procedimento", label: "Desvio ao Procedimento" },
            ]},
            { key: "origin", label: "Origem", type: "select", required: true, options: [
              { value: "interna", label: "Interna" }, { value: "fornecedor", label: "Fornecedor" },
              { value: "cliente", label: "Cliente" }, { value: "auditoria", label: "Auditoria" },
            ]},
            { key: "severity", label: "Severidade", type: "select", required: true, options: [
              { value: "menor", label: "Menor" }, { value: "maior", label: "Maior" }, { value: "critica", label: "Crítica" },
            ]},
            { key: "product_or_process", label: "Produto/Processo Afetado" },
            { key: "order_code", label: "Ordem de Produção" },
            { key: "responsible", label: "Responsável" },
            { key: "department", label: "Departamento" },
            { key: "root_cause", label: "Causa Raiz", type: "textarea" },
            { key: "immediate_action", label: "Ação Imediata", type: "textarea" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "aberta", label: "Aberta" }, { value: "em_analise", label: "Em Análise" },
              { value: "em_tratamento", label: "Em Tratamento" }, { value: "fechada", label: "Fechada" }, { value: "cancelada", label: "Cancelada" },
            ]},
          ]}
        />
      )}

      {activeTab === "capas" && (
        <CrudModule title="Ação Corretiva" icon={CheckCircle2} endpoint="corrective-actions"
          searchFields={["code","title","responsible","description"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "action_type", label: "Tipo" },
            { key: "responsible", label: "Responsável" },
            { key: "deadline", label: "Prazo", render: v => v ? new Date(v).toLocaleDateString("pt-PT") : "-" },
            { key: "status", label: "Estado" },
            { key: "effectiveness", label: "Eficácia" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "description", label: "Descrição", type: "textarea", required: true },
            { key: "action_type", label: "Tipo", type: "select", options: [
              { value: "corretiva", label: "Corretiva" }, { value: "preventiva", label: "Preventiva" }, { value: "melhoria", label: "Melhoria" },
            ]},
            { key: "nc_id", label: "NC Associada (código)" },
            { key: "root_cause_analysis", label: "Análise Causa Raiz", type: "textarea" },
            { key: "planned_action", label: "Ação Planeada", type: "textarea", required: true },
            { key: "responsible", label: "Responsável", required: true },
            { key: "department", label: "Departamento" },
            { key: "deadline", label: "Prazo", type: "date" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "aberta", label: "Aberta" }, { value: "em_curso", label: "Em Curso" },
              { value: "concluida", label: "Concluída" }, { value: "verificada", label: "Verificada" }, { value: "cancelada", label: "Cancelada" },
            ]},
            { key: "verification_result", label: "Resultado Verificação", type: "textarea" },
            { key: "effectiveness", label: "Eficácia", type: "select", options: [
              { value: "", label: "Pendente" }, { value: "eficaz", label: "Eficaz" },
              { value: "parcialmente_eficaz", label: "Parcialmente Eficaz" }, { value: "ineficaz", label: "Ineficaz" },
            ]},
          ]}
        />
      )}

      {activeTab === "audits" && (
        <CrudModule title="Auditoria" icon={ClipboardCheck} endpoint="audits"
          searchFields={["code","title","auditor","auditee_department"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "audit_type", label: "Tipo" },
            { key: "auditor", label: "Auditor" },
            { key: "planned_date", label: "Data", render: v => v ? new Date(v).toLocaleDateString("pt-PT") : "-" },
            { key: "status", label: "Estado" },
            { key: "score", label: "Pontuação" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "audit_type", label: "Tipo", type: "select", required: true, options: [
              { value: "interna", label: "Interna" }, { value: "externa", label: "Externa" },
              { value: "fornecedor", label: "Fornecedor" }, { value: "cliente", label: "Cliente" },
            ]},
            { key: "scope", label: "Âmbito", type: "textarea" },
            { key: "auditor", label: "Auditor", required: true },
            { key: "auditee_department", label: "Departamento Auditado" },
            { key: "planned_date", label: "Data Planeada", type: "date", required: true },
            { key: "actual_date", label: "Data Real", type: "date" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "planeada", label: "Planeada" }, { value: "em_curso", label: "Em Curso" },
              { value: "concluida", label: "Concluída" }, { value: "cancelada", label: "Cancelada" },
            ]},
            { key: "conclusion", label: "Conclusão", type: "textarea" },
            { key: "score", label: "Pontuação (0-100)", type: "number" },
          ]}
        />
      )}

      {activeTab === "complaints" && (
        <CrudModule title="Reclamação" icon={MessageSquareWarning} endpoint="complaints"
          searchFields={["code","customer_name","product","description"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "customer_name", label: "Cliente" },
            { key: "product", label: "Produto" },
            { key: "complaint_date", label: "Data", render: v => v ? new Date(v).toLocaleDateString("pt-PT") : "-" },
            { key: "severity", label: "Severidade" },
            { key: "status", label: "Estado" },
          ]}
          formFields={[
            { key: "customer_name", label: "Cliente", required: true },
            { key: "contact", label: "Contacto" },
            { key: "product", label: "Produto" },
            { key: "order_code", label: "Ordem de Produção" },
            { key: "lot_number", label: "Lote" },
            { key: "complaint_date", label: "Data da Reclamação", type: "date", required: true },
            { key: "description", label: "Descrição", type: "textarea", required: true },
            { key: "severity", label: "Severidade", type: "select", options: [
              { value: "menor", label: "Menor" }, { value: "maior", label: "Maior" }, { value: "critica", label: "Crítica" },
            ]},
            { key: "root_cause", label: "Causa Raiz", type: "textarea" },
            { key: "response_to_customer", label: "Resposta ao Cliente", type: "textarea" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "aberta", label: "Aberta" }, { value: "em_analise", label: "Em Análise" },
              { value: "respondida", label: "Respondida" }, { value: "fechada", label: "Fechada" },
            ]},
          ]}
        />
      )}

      {activeTab === "suppliers" && (
        <CrudModule title="Fornecedor" icon={Truck} endpoint="suppliers"
          searchFields={["supplier_name","category","contact"]}
          columns={[
            { key: "supplier_name", label: "Nome" },
            { key: "category", label: "Categoria" },
            { key: "rating", label: "Avaliação", render: v => "★".repeat(v || 0) + "☆".repeat(5 - (v || 0)) },
            { key: "status", label: "Estado" },
            { key: "last_evaluation", label: "Última Aval.", render: v => v ? new Date(v).toLocaleDateString("pt-PT") : "-" },
          ]}
          formFields={[
            { key: "supplier_name", label: "Nome", required: true },
            { key: "category", label: "Categoria", type: "select", options: [
              { value: "materia_prima", label: "Matéria-Prima" }, { value: "servicos", label: "Serviços" }, { value: "equipamentos", label: "Equipamentos" },
            ]},
            { key: "contact", label: "Contacto" },
            { key: "rating", label: "Avaliação (1-5)", type: "number", placeholder: "3" },
            { key: "quality_score", label: "Pontuação Qualidade (0-100)", type: "number" },
            { key: "delivery_score", label: "Pontuação Entrega (0-100)", type: "number" },
            { key: "price_score", label: "Pontuação Preço (0-100)", type: "number" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "ativo", label: "Ativo" }, { value: "em_avaliacao", label: "Em Avaliação" },
              { value: "suspenso", label: "Suspenso" }, { value: "bloqueado", label: "Bloqueado" },
            ]},
            { key: "notes", label: "Notas", type: "textarea" },
          ]}
        />
      )}

      {activeTab === "calibration" && (
        <CrudModule title="Equipamento" icon={Ruler} endpoint="calibration"
          searchFields={["code","name","equipment_type","serial_number"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "name", label: "Nome" },
            { key: "equipment_type", label: "Tipo" },
            { key: "next_calibration", label: "Próx. Calibração", render: (v) => {
              if (!v) return "-";
              const d = new Date(v); const now = new Date();
              const overdue = d < now;
              return <span className={overdue ? "text-red-600 font-bold" : ""}>{d.toLocaleDateString("pt-PT")}{overdue ? " VENCIDA" : ""}</span>;
            }},
            { key: "status", label: "Estado" },
          ]}
          formFields={[
            { key: "code", label: "Código", required: true },
            { key: "name", label: "Nome", required: true },
            { key: "equipment_type", label: "Tipo", type: "select", options: [
              { value: "calibre", label: "Calibre" }, { value: "micrometro", label: "Micrómetro" },
              { value: "paquimetro", label: "Paquímetro" }, { value: "balanca", label: "Balança" },
              { value: "termometro", label: "Termómetro" }, { value: "comparador", label: "Comparador" }, { value: "outro", label: "Outro" },
            ]},
            { key: "serial_number", label: "Número de Série" },
            { key: "location", label: "Localização" },
            { key: "last_calibration", label: "Última Calibração", type: "date" },
            { key: "next_calibration", label: "Próxima Calibração", type: "date" },
            { key: "calibration_interval_days", label: "Intervalo (dias)", type: "number", placeholder: "365" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "conforme", label: "Conforme" }, { value: "nao_conforme", label: "Não Conforme" },
              { value: "em_calibracao", label: "Em Calibração" }, { value: "fora_servico", label: "Fora de Serviço" },
            ]},
          ]}
        />
      )}

      {activeTab === "risks" && (
        <CrudModule title="Risco" icon={Shield} endpoint="risks"
          searchFields={["title","description","category","responsible"]}
          columns={[
            { key: "title", label: "Título" },
            { key: "category", label: "Categoria" },
            { key: "probability", label: "Prob.", render: v => `${v}/5` },
            { key: "impact", label: "Imp.", render: v => `${v}/5` },
            { key: "risk_level", label: "Nível", render: (v) => <Badge className={`${riskColors[v] || "bg-slate-400"} text-white text-xs`}>{(v||"").toUpperCase()}</Badge> },
            { key: "status", label: "Estado" },
            { key: "responsible", label: "Responsável" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "description", label: "Descrição", type: "textarea", required: true },
            { key: "category", label: "Categoria", type: "select", options: [
              { value: "processo", label: "Processo" }, { value: "produto", label: "Produto" },
              { value: "fornecedor", label: "Fornecedor" }, { value: "seguranca", label: "Segurança" }, { value: "ambiente", label: "Ambiente" },
            ]},
            { key: "probability", label: "Probabilidade (1-5)", type: "number", placeholder: "1" },
            { key: "impact", label: "Impacto (1-5)", type: "number", placeholder: "1" },
            { key: "mitigation_action", label: "Ação de Mitigação", type: "textarea" },
            { key: "responsible", label: "Responsável" },
            { key: "deadline", label: "Prazo", type: "date" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "identificado", label: "Identificado" }, { value: "em_tratamento", label: "Em Tratamento" },
              { value: "mitigado", label: "Mitigado" }, { value: "aceite", label: "Aceite" },
            ]},
          ]}
        />
      )}

      {activeTab === "product-sheets" && (
        <CrudModule title="Ficha de Produto" icon={FileText} endpoint="product-sheets" enableImages enablePdf
          searchFields={["code","title","part_code","customer"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "part_code", label: "Peça" },
            { key: "customer", label: "Cliente" },
            { key: "material", label: "Material" },
            { key: "status", label: "Estado" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "part_code", label: "Código da Peça" },
            { key: "customer", label: "Cliente" },
            { key: "material", label: "Material (tipo plástico)" },
            { key: "weight_nominal", label: "Peso Nominal (g)", type: "number" },
            { key: "weight_tolerance", label: "Tolerância (%)", type: "number", placeholder: "5" },
            { key: "dimensions", label: "Dimensões Críticas", type: "textarea", placeholder: "Ex: Comprimento 120±0.5mm, Largura 50±0.3mm" },
            { key: "color", label: "Cor / RAL" },
            { key: "surface_finish", label: "Acabamento Superficial" },
            { key: "special_requirements", label: "Requisitos Especiais", type: "textarea" },
            { key: "packaging", label: "Embalagem / Acondicionamento" },
            { key: "engineering_notes", label: "Notas de Engenharia", type: "textarea" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "rascunho", label: "Rascunho" }, { value: "aprovado", label: "Aprovado" }, { value: "obsoleto", label: "Obsoleto" },
            ]},
            { key: "approved_by", label: "Aprovado por" },
          ]}
        />
      )}

      {activeTab === "fmea" && (
        <CrudModule title="FMEA" icon={AlertTriangle} endpoint="fmea" enablePdf
          searchFields={["code","title","process","part_code"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "fmea_type", label: "Tipo" },
            { key: "process", label: "Processo" },
            { key: "rpn_max", label: "RPN Máx", render: v => <span className={`font-bold ${(v||0) > 100 ? "text-red-600" : (v||0) > 50 ? "text-amber-600" : "text-green-600"}`}>{v||"-"}</span> },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "fmea_type", label: "Tipo", type: "select", required: true, options: [
              { value: "processo", label: "FMEA de Processo" }, { value: "projeto", label: "FMEA de Projeto" }, { value: "produto", label: "FMEA de Produto" },
            ]},
            { key: "part_code", label: "Código da Peça" },
            { key: "process", label: "Processo / Operação" },
            { key: "failure_mode", label: "Modo de Falha", type: "textarea", required: true },
            { key: "failure_effect", label: "Efeito da Falha", type: "textarea" },
            { key: "failure_cause", label: "Causa da Falha", type: "textarea" },
            { key: "severity", label: "Severidade (1-10)", type: "number", placeholder: "1" },
            { key: "occurrence", label: "Ocorrência (1-10)", type: "number", placeholder: "1" },
            { key: "detection", label: "Detecção (1-10)", type: "number", placeholder: "1" },
            { key: "rpn_max", label: "RPN (S×O×D)", type: "number" },
            { key: "current_controls", label: "Controlos Atuais", type: "textarea" },
            { key: "recommended_action", label: "Ação Recomendada", type: "textarea" },
            { key: "responsible", label: "Responsável" },
          ]}
        />
      )}

      {activeTab === "control-plans" && (
        <CrudModule title="Plano de Controlo" icon={ClipboardCheck} endpoint="control-plans" enableImages enablePdf
          searchFields={["code","title","part_code","process"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "part_code", label: "Peça" },
            { key: "process", label: "Processo" },
            { key: "status", label: "Estado" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "part_code", label: "Peça / Produto" },
            { key: "process", label: "Processo" },
            { key: "characteristic", label: "Característica a Controlar", type: "textarea", placeholder: "Ex: Peso, Dimensão X, Cor..." },
            { key: "specification", label: "Especificação / Tolerância" },
            { key: "method", label: "Método de Controlo", type: "select", options: [
              { value: "visual", label: "Visual" }, { value: "dimensional", label: "Dimensional" },
              { value: "peso", label: "Pesagem" }, { value: "funcional", label: "Funcional" }, { value: "laboratorial", label: "Laboratorial" },
            ]},
            { key: "instrument", label: "Instrumento de Medição" },
            { key: "frequency", label: "Frequência de Controlo", placeholder: "Ex: 1 em cada 50 peças" },
            { key: "sample_size", label: "Tamanho da Amostra" },
            { key: "reaction_plan", label: "Plano de Reação (se NOK)", type: "textarea" },
            { key: "responsible", label: "Responsável" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "rascunho", label: "Rascunho" }, { value: "aprovado", label: "Aprovado" }, { value: "obsoleto", label: "Obsoleto" },
            ]},
          ]}
        />
      )}

      {activeTab === "spc" && (
        <CrudModule title="Medição SPC" icon={Ruler} endpoint="spc-measurements"
          searchFields={["part_code","characteristic","operator"]}
          columns={[
            { key: "part_code", label: "Peça" },
            { key: "characteristic", label: "Característica" },
            { key: "value", label: "Valor", render: v => <span className="font-mono font-bold">{v}</span> },
            { key: "unit", label: "Unidade" },
            { key: "operator", label: "Operador" },
            { key: "created_at", label: "Data", render: v => v ? new Date(v).toLocaleString("pt-PT", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : "-" },
          ]}
          formFields={[
            { key: "part_code", label: "Código da Peça", required: true },
            { key: "order_id", label: "Ordem de Produção" },
            { key: "characteristic", label: "Característica", required: true, placeholder: "Ex: Peso, Espessura, Comprimento" },
            { key: "value", label: "Valor Medido", type: "number", required: true },
            { key: "unit", label: "Unidade", placeholder: "g, mm, etc." },
            { key: "instrument", label: "Instrumento Utilizado" },
            { key: "operator", label: "Operador" },
            { key: "notes", label: "Observações" },
          ]}
        />
      )}

      {activeTab === "8d" && (
        <CrudModule title="Relatório 8D" icon={Shield} endpoint="8d-reports" enablePdf
          searchFields={["code","title","customer","team_leader"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título / Problema" },
            { key: "customer", label: "Cliente" },
            { key: "team_leader", label: "Líder Equipa" },
            { key: "status", label: "Estado" },
          ]}
          formFields={[
            { key: "title", label: "D1 - Descrição do Problema", type: "textarea", required: true },
            { key: "customer", label: "Cliente Afetado" },
            { key: "product", label: "Produto / Peça" },
            { key: "team_leader", label: "D2 - Líder da Equipa", required: true },
            { key: "team_members", label: "D2 - Membros da Equipa" },
            { key: "containment_action", label: "D3 - Ação de Contenção", type: "textarea" },
            { key: "root_cause", label: "D4 - Causa Raiz", type: "textarea" },
            { key: "corrective_action", label: "D5 - Ação Corretiva Permanente", type: "textarea" },
            { key: "verification", label: "D6 - Verificação da Eficácia", type: "textarea" },
            { key: "preventive_action", label: "D7 - Ação Preventiva", type: "textarea" },
            { key: "congratulations", label: "D8 - Reconhecimento da Equipa" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "aberto", label: "Aberto" }, { value: "em_curso", label: "Em Curso" },
              { value: "concluido", label: "Concluído" }, { value: "verificado", label: "Verificado" },
            ]},
          ]}
        />
      )}

      {activeTab === "costs" && (
        <CrudModule title="Custo" icon={LayoutDashboard} endpoint="quality-costs"
          searchFields={["description","cost_type","nc_code"]}
          columns={[
            { key: "cost_type", label: "Tipo" },
            { key: "description", label: "Descrição" },
            { key: "nc_code", label: "NC Associada" },
            { key: "amount", label: "Valor", render: v => <span className="font-mono font-bold">{(v||0).toFixed(2)} EUR</span> },
            { key: "created_at", label: "Data", render: v => v ? new Date(v).toLocaleDateString("pt-PT") : "-" },
          ]}
          formFields={[
            { key: "cost_type", label: "Tipo de Custo", type: "select", required: true, options: [
              { value: "nao_qualidade", label: "Custo de Não-Qualidade" }, { value: "prevencao", label: "Custo de Prevenção" },
              { value: "avaliacao", label: "Custo de Avaliação" }, { value: "falha_interna", label: "Falha Interna" },
              { value: "falha_externa", label: "Falha Externa" }, { value: "retrabalho", label: "Retrabalho" },
              { value: "sucata", label: "Sucata" }, { value: "garantia", label: "Garantia" },
            ]},
            { key: "description", label: "Descrição", required: true },
            { key: "nc_code", label: "NC Associada" },
            { key: "order_code", label: "Ordem de Produção" },
            { key: "amount", label: "Valor (EUR)", type: "number", required: true },
            { key: "department", label: "Departamento" },
          ]}
        />
      )}

      {activeTab === "improvements" && (
        <CrudModule title="Plano de Melhoria" icon={CheckCircle2} endpoint="improvement-plans" enablePdf
          searchFields={["code","title","responsible","department"]}
          columns={[
            { key: "code", label: "Código", mono: true },
            { key: "title", label: "Título" },
            { key: "responsible", label: "Responsável" },
            { key: "deadline", label: "Prazo", render: v => v ? new Date(v).toLocaleDateString("pt-PT") : "-" },
            { key: "status", label: "Estado" },
          ]}
          formFields={[
            { key: "title", label: "Título", required: true },
            { key: "description", label: "Descrição da Melhoria", type: "textarea", required: true },
            { key: "objective", label: "Objetivo / Resultado Esperado", type: "textarea" },
            { key: "department", label: "Departamento" },
            { key: "responsible", label: "Responsável", required: true },
            { key: "deadline", label: "Prazo", type: "date" },
            { key: "resources", label: "Recursos Necessários" },
            { key: "kpi", label: "Indicador de Sucesso (KPI)" },
            { key: "status", label: "Estado", type: "select", options: [
              { value: "proposto", label: "Proposto" }, { value: "aprovado", label: "Aprovado" },
              { value: "em_curso", label: "Em Curso" }, { value: "concluido", label: "Concluído" }, { value: "cancelado", label: "Cancelado" },
            ]},
          ]}
        />
      )}
    </div>
  );
}
